import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { TrendingDown, ShoppingCart, Smartphone, AlertTriangle, Clock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import eyecaptainIcon from '@/assets/eyecaptain-icon-optimized.webp';

const BANNER_VARIANTS = [
  {
    id: 'low-conversions',
    keywords: ['cro', 'conversion', 'conversions', 'conversion rate', 'a/b testing', 'split test', 'optimization', 'funnel', 'lead generation', 'leads'],
    icon: TrendingDown,
    stat: '97%',
    statLabel: { en: "of visitors don't convert", el: 'των επισκεπτών δεν μετατρέπονται' },
    headline: { en: 'Your visitors leave without converting', el: 'Οι επισκέπτες σου φεύγουν χωρίς να μετατραπούν' },
    description: {
      en: 'Most websites lose 97% of their traffic without a single conversion. Our AI scans 200+ CRO elements to find exactly where visitors drop off.',
      el: 'Τα περισσότερα sites χάνουν το 97% της επισκεψιμότητάς τους χωρίς conversion. Το AI μας σκανάρει 200+ CRO στοιχεία για να βρει ακριβώς πού φεύγουν οι επισκέπτες.',
    },
    accentColor: '#22c55e',
    pulseItems: ['Hero CTA missing', 'Form friction detected', 'No social proof'],
    pulseItemsEl: ['Λείπει Hero CTA', 'Friction στη φόρμα', 'Χωρίς social proof'],
  },
  {
    id: 'high-bounce',
    keywords: ['bounce', 'bounce rate', 'engagement', 'session', 'time on page', 'exit rate', 'analytics', 'web analytics', 'seo', 'traffic'],
    icon: Clock,
    stat: '3sec',
    statLabel: { en: 'and they bounce forever', el: 'και φεύγουν για πάντα' },
    headline: { en: 'Users decide in 3 seconds — are you losing them?', el: 'Οι χρήστες αποφασίζουν σε 3 δευτερόλεπτα — τους χάνεις;' },
    description: {
      en: 'If your page doesn\'t hook visitors instantly, they\'re gone. Get a full breakdown of what\'s causing friction above the fold.',
      el: 'Αν η σελίδα σου δεν τραβήξει τους επισκέπτες αμέσως, φεύγουν. Πάρε πλήρη ανάλυση για τι προκαλεί friction στο above the fold.',
    },
    accentColor: '#eab308',
    pulseItems: ['Slow LCP detected', 'Unclear value prop', 'Navigation confusion'],
    pulseItemsEl: ['Αργό LCP', 'Ασαφές value proposition', 'Σύγχυση πλοήγησης'],
  },
  {
    id: 'cart-abandonment',
    keywords: ['ecommerce', 'e-commerce', 'cart', 'checkout', 'shopping', 'product page', 'buy', 'purchase', 'order', 'pricing', 'payment'],
    icon: ShoppingCart,
    stat: '-35%',
    statLabel: { en: 'cart abandonment is avoidable', el: 'της εγκατάλειψης καλαθιού αποφεύγεται' },
    headline: { en: 'Your checkout is leaking revenue', el: 'Το checkout σου χάνει έσοδα' },
    description: {
      en: '35% of cart abandonments are caused by fixable UX issues. Our audit reveals trust gaps, hidden costs, and checkout friction points.',
      el: 'Το 35% της εγκατάλειψης καλαθιού οφείλεται σε διορθώσιμα UX ζητήματα. Η ανάλυσή μας αποκαλύπτει trust gaps, κρυφά κόστη και friction σημεία.',
    },
    accentColor: '#22c55e',
    pulseItems: ['Trust signals missing', 'Complex checkout', 'Hidden costs detected'],
    pulseItemsEl: ['Λείπουν trust signals', 'Πολύπλοκο checkout', 'Κρυφά κόστη'],
  },
  {
    id: 'poor-ux',
    keywords: ['ux', 'user experience', 'usability', 'accessibility', 'design', 'ui', 'interface', 'navigation', 'heuristic', 'neuromarketing', 'psychology'],
    icon: AlertTriangle,
    stat: '88%',
    statLabel: { en: "won't return after bad UX", el: 'δεν επιστρέφουν μετά από κακό UX' },
    headline: { en: 'UX bugs are silently killing your revenue', el: 'Τα UX bugs σκοτώνουν σιωπηλά τα έσοδά σου' },
    description: {
      en: '88% of users never come back after a bad experience. Our AI detects CTA hierarchy issues, mobile gaps, and cognitive overload patterns.',
      el: 'Το 88% των χρηστών δεν επιστρέφει μετά από κακή εμπειρία. Το AI μας εντοπίζει θέματα CTA ιεραρχίας, mobile κενά και cognitive overload.',
    },
    accentColor: '#22c55e',
    pulseItems: ['CTA hierarchy broken', 'Mobile UX gaps', 'Cognitive overload'],
    pulseItemsEl: ['Σπασμένη CTA ιεραρχία', 'Mobile UX κενά', 'Cognitive overload'],
  },
  {
    id: 'invisible-issues',
    keywords: ['performance', 'speed', 'loading', 'technical', 'pagespeed', 'core web vitals', 'lighthouse', 'audit', 'analysis', 'ai', 'digital marketing'],
    icon: Smartphone,
    stat: '23+',
    statLabel: { en: 'invisible CRO issues on avg', el: 'αόρατα CRO issues κατά μ.ό.' },
    headline: { en: "Problems you can't see are costing you sales", el: 'Προβλήματα που δεν βλέπεις σου κοστίζουν πωλήσεις' },
    description: {
      en: 'The average website has 23+ hidden conversion killers. From slow load times to invisible UX bugs — our AI finds what humans miss.',
      el: 'Ο μέσος ιστότοπος έχει 23+ κρυφά conversion killers. Από αργούς χρόνους φόρτωσης μέχρι αόρατα UX bugs — το AI μας βρίσκει ό,τι ξεφεύγει.',
    },
    accentColor: '#eab308',
    pulseItems: ['Mobile-first gaps', 'CRO score: unknown', 'Competitor advantage'],
    pulseItemsEl: ['Mobile-first κενά', 'CRO score: άγνωστο', 'Πλεονέκτημα ανταγωνιστών'],
  },
];

function matchBanner(category?: string, keywords?: string[]): typeof BANNER_VARIANTS[0] {
  if (!category && (!keywords || keywords.length === 0)) {
    return BANNER_VARIANTS[0];
  }

  const articleTerms = [
    ...(category ? [category.toLowerCase()] : []),
    ...(keywords ? keywords.map(k => k.toLowerCase()) : []),
  ].join(' ');

  let bestMatch = BANNER_VARIANTS[0];
  let bestScore = 0;

  for (const variant of BANNER_VARIANTS) {
    let score = 0;
    for (const kw of variant.keywords) {
      if (articleTerms.includes(kw)) score++;
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = variant;
    }
  }

  return bestMatch;
}

interface ConversionsBoosterBannerProps {
  category?: string;
  keywords?: string[];
}

export function ConversionsBoosterBanner({ category, keywords }: ConversionsBoosterBannerProps) {
  const { language } = useLanguage();
  const banner = useMemo(() => matchBanner(category, keywords), [category, keywords]);
  const [currentPulse, setCurrentPulse] = useState(0);
  const [showStat, setShowStat] = useState(false);

  const pulseItems = language === 'el' ? banner.pulseItemsEl : banner.pulseItems;
  const lang = language === 'el' ? 'el' : 'en';

  useEffect(() => {
    const statTimer = setTimeout(() => setShowStat(true), 600);
    const pulseInterval = setInterval(() => {
      setCurrentPulse(prev => (prev + 1) % pulseItems.length);
    }, 2200);

    return () => {
      clearTimeout(statTimer);
      clearInterval(pulseInterval);
    };
  }, [pulseItems.length]);

  const Icon = banner.icon;

  return (
    <div className="my-12 rounded-xl overflow-hidden border border-foreground/10 bg-gradient-to-r from-background via-card to-background relative group">
      {/* Accent glow */}
      <div
        className="absolute -top-10 -right-10 w-52 h-52 rounded-full blur-[100px] opacity-20 transition-opacity group-hover:opacity-30"
        style={{ backgroundColor: banner.accentColor }}
      />
      <div
        className="absolute -bottom-10 -left-10 w-36 h-36 rounded-full blur-[80px] opacity-10"
        style={{ backgroundColor: banner.accentColor }}
      />

      {/* EyeCaptain badge */}
      <div className="flex items-center gap-2 px-5 pt-4 relative z-10">
        <img src={eyecaptainIcon} alt="EyeCaptain" className="w-4 h-4 rounded-sm" />
        <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">EyeCaptain</span>
      </div>

      <div className="relative z-10 px-5 pt-3 pb-5 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        {/* Animated stat circle */}
        <div className="shrink-0 relative">
          <motion.div
            className="w-20 h-20 rounded-full flex flex-col items-center justify-center border-2 relative overflow-hidden"
            style={{ borderColor: `${banner.accentColor}40` }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                background: `conic-gradient(${banner.accentColor} 0deg, transparent 120deg, transparent 360deg)`,
                opacity: 0.2,
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
            />
            <AnimatePresence mode="wait">
              {showStat && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  className="flex flex-col items-center z-10"
                >
                  <span className="text-xl font-black" style={{ color: banner.accentColor }}>{banner.stat}</span>
                  <Icon className="h-3.5 w-3.5 text-white/40" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2"
            style={{ backgroundColor: `${banner.accentColor}15`, color: banner.accentColor }}
          >
            <Icon className="h-2.5 w-2.5" />
            Conversions Booster
          </div>

          {/* Headline */}
          <p className="text-base font-bold text-white/90 leading-snug mb-1.5">
            {banner.headline[lang]}
          </p>

          {/* Description */}
          <p className="text-sm text-white/50 leading-relaxed mb-2">
            {banner.description[lang]}
          </p>

          {/* Cycling pulse alerts */}
          <div className="h-4 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.p
                key={currentPulse}
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -12, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-xs text-white/40 flex items-center gap-1.5"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: banner.accentColor }} />
                {pulseItems[currentPulse]}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        {/* CTA - desktop */}
        <div className="shrink-0 hidden md:block">
          <Button
            asChild
            size="default"
            className="rounded-lg font-bold text-sm shadow-lg px-6 text-black"
            style={{
              backgroundColor: banner.accentColor,
              boxShadow: `0 4px 24px ${banner.accentColor}30`,
            }}
          >
             <Link to={"/free-audit"}>
              {lang === 'el' ? 'Free Website Audit' : 'Free Website Audit'}

            </Link>
          </Button>
        </div>
      </div>

      {/* Mobile CTA */}
      <div className="md:hidden px-5 pb-5">
        <Button
          asChild
          size="default"
          className="w-full rounded-lg font-bold text-sm text-black"
          style={{ backgroundColor: banner.accentColor }}
        >
          <Link to={"/free-audit"}>
            {lang === 'el' ? 'Free Website Audit' : 'Free Website Audit'}

          </Link>
        </Button>
      </div>
    </div>
  );
}
